/**
 * Common sentences (Redemittel) overview page component.
 *
 * Displays common German phrases and expressions organized by proficiency level
 * and category. Provides search functionality, level filtering, bookmarking,
 * and copy-to-clipboard functionality for phrases.
 *
 * Route: `/learning/sentences`
 * Requires authentication: Yes
 *
 * @module CommonSentencesOverview
 */

import { useState, useMemo, useEffect } from "react";
import {
  ChevronDown,
  ChevronUp,
  Copy,
  Bookmark,
  BookmarkCheck,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQueries } from "@tanstack/react-query";
import { SearchBar } from "@/components/learning/SearchBar";
import {
  loadRedemittelContent,
  type RedemittelCategory,
} from "@/lib/contentLoader";
import { grammarLevels } from "@/data/learningData";
import { ProficiencyLevel } from "@/types/learning";
import { toast } from "@/components/ui/sonner";
import { loadSentenceBookmarks, saveSentenceBookmarks } from "@/lib/storage";
import { sanitizeHtml } from "@/lib/sanitize";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { StandardPageShell } from "@/components/layout/StandardPageShell";
import { LearningSectionSwitcher } from "@/components/learning/LearningSectionSwitcher";
import { cn } from "@/lib/utils";

interface SentenceLevelData {
  level: ProficiencyLevel;
  categories: RedemittelCategory[];
}

type SentenceLevelRow =
  | {
      kind: "content";
      level: ProficiencyLevel;
      categories: RedemittelCategory[];
    }
  | { kind: "comingSoon"; level: ProficiencyLevel };

// Phrase item component
interface PhraseItemProps {
  phrase: { german: string; english: string };
  phraseId: string;
  onCopy: (text: string) => void;
  onBookmarkToggle: (id: string) => void;
  isBookmarked: boolean;
}

// Helper function to convert markdown formatting to HTML
function formatMarkdown(text: string): string {
  return text
    .replaceAll(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replaceAll(/<u>(.*?)<\/u>/g, "<u>$1</u>")
    .replaceAll("\n", "<br>");
}

function PhraseItem({
  phrase,
  phraseId,
  onCopy,
  onBookmarkToggle,
  isBookmarked,
}: Readonly<PhraseItemProps>) {
  const formattedGerman = formatMarkdown(phrase.german);
  const formattedEnglish = phrase.english ? formatMarkdown(phrase.english) : "";

  // Sanitize HTML to prevent XSS attacks while preserving formatting
  const safeGerman = sanitizeHtml(formattedGerman);
  const safeEnglish = formattedEnglish ? sanitizeHtml(formattedEnglish) : "";

  return (
    <div className="flex min-h-[56px] flex-col justify-between gap-3 border-b border-border p-3 transition-colors last:border-b-0 hover:bg-accent/25 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
      <div className="min-w-0 flex-1">
        <p
          className="font-ui font-medium text-base text-foreground mb-1"
          dangerouslySetInnerHTML={{ __html: safeGerman }}
        />
        {safeEnglish && (
          <p
            className="font-ui text-sm text-muted-foreground"
            dangerouslySetInnerHTML={{ __html: safeEnglish }}
          />
        )}
      </div>
      <div className="flex items-center justify-end gap-1 sm:ml-4 sm:gap-2">
        <button
          onClick={() =>
            onCopy(
              phrase.german.replaceAll("**", "").replaceAll(/<u>|<\/u>/g, ""),
            )
          }
          className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-accent lg:h-9 lg:w-9"
          aria-label="Copy"
        >
          <Copy className="h-4 w-4 text-muted-foreground" />
        </button>
        <button
          onClick={() => onBookmarkToggle(phraseId)}
          className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-accent lg:h-9 lg:w-9"
          aria-label="Bookmark"
        >
          {isBookmarked ? (
            <BookmarkCheck className="h-4 w-4 text-primary" />
          ) : (
            <Bookmark className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </div>
    </div>
  );
}

const LEVEL_ORDER: ProficiencyLevel[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

function levelDisplayName(level: ProficiencyLevel): string {
  return grammarLevels.find((g) => g.level === level)?.name ?? level;
}

export default function CommonSentencesOverview() {
  const { t } = useTranslation();
  const [selectedLevels, setSelectedLevels] = useState<ProficiencyLevel[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedLevels, setExpandedLevels] = useState<Set<ProficiencyLevel>>(
    new Set(["A1"]),
  );
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  );
  const [bookmarkedPhrases, setBookmarkedPhrases] = useState<Set<string>>(
    loadSentenceBookmarks(),
  );

  const lockedLevels = useMemo(
    () => new Set<ProficiencyLevel>(["C1", "C2"]),
    [],
  );

  const isLevelLocked = (level: ProficiencyLevel) => lockedLevels.has(level);

  // Load Redemittel content for all levels using React Query with caching
  const levelQueries = useQueries({
    queries: LEVEL_ORDER.map((level) => ({
      queryKey: ["redemittelContent", level],
      queryFn: () => loadRedemittelContent(level),
      staleTime: 1000 * 60 * 30, // 30 minutes
      gcTime: 1000 * 60 * 60, // 1 hour
    })),
  });

  // Transform query results into SentenceLevelData format
  const sentenceLevels: SentenceLevelData[] = useMemo(() => {
    const loadedLevels: SentenceLevelData[] = [];
    levelQueries.forEach((query, index) => {
      if (query.data && query.data.length > 0) {
        loadedLevels.push({
          level: LEVEL_ORDER[index],
          categories: query.data,
        });
      }
    });
    return loadedLevels;
  }, [levelQueries]);

  // Determine loading state (any query is still loading)
  const loading = levelQueries.some((query) => query.isLoading);

  // Update bookmarks in localStorage when they change
  useEffect(() => {
    saveSentenceBookmarks(bookmarkedPhrases);
  }, [bookmarkedPhrases]);

  /** Order A1→C2: loaded catalogs plus C1/C2 placeholders when empty (locked). */
  const sentenceLevelRows: SentenceLevelRow[] = useMemo(() => {
    const byLevel = new Map(sentenceLevels.map((s) => [s.level, s.categories]));
    const rows: SentenceLevelRow[] = [];
    for (const level of LEVEL_ORDER) {
      const categories = byLevel.get(level) ?? [];
      if (isLevelLocked(level) && categories.length === 0) {
        rows.push({ kind: "comingSoon", level });
      } else if (categories.length > 0) {
        rows.push({ kind: "content", level, categories });
      }
    }
    return rows;
  }, [sentenceLevels, lockedLevels]);

  const filteredLevelRows = useMemo(() => {
    if (selectedLevels.length === 0) return sentenceLevelRows;
    return sentenceLevelRows.filter((row) =>
      selectedLevels.includes(row.level),
    );
  }, [sentenceLevelRows, selectedLevels]);

  const handleLevelToggle = (level: ProficiencyLevel) => {
    setSelectedLevels((prev) =>
      prev.includes(level) ? prev.filter((l) => l !== level) : [...prev, level],
    );
  };

  const handleClearLevels = () => {
    setSelectedLevels([]);
  };

  /** Collapse/expand level accordions for levels that have Redemittel data (not gated by marketing locks). */
  const toggleLevelExpanded = (level: ProficiencyLevel) => {
    setExpandedLevels((prev) => {
      const next = new Set(prev);
      if (next.has(level)) {
        next.delete(level);
      } else {
        next.add(level);
      }
      return next;
    });
  };

  const toggleCategoryExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t("common.copied", { defaultValue: "Copied to clipboard" }));
  };

  const handleBookmarkToggle = (phraseId: string) => {
    setBookmarkedPhrases((prev) => {
      const next = new Set(prev);
      if (next.has(phraseId)) {
        next.delete(phraseId);
      } else {
        next.add(phraseId);
      }
      return next;
    });
  };

  // Generate phrase ID from German text
  const getPhraseId = (
    german: string,
    categoryId: string,
    index: number,
  ): string => {
    return `${categoryId}-${index}-${german.slice(0, 20).replaceAll(/\s+/g, "-")}`;
  };

  // Parse a single sentence into a phrase object
  const parseSentence = (
    sentence: string,
  ): { german: string; english: string } => {
    const parts = sentence.split(" - ").map((s) => s.trim());
    if (parts.length >= 2) {
      return {
        german: parts[0],
        english: parts.slice(1).join(" - "),
      };
    }
    return {
      german: sentence,
      english: "",
    };
  };

  // Parse sentences from category into phrase objects
  const parseCategoryPhrases = (
    category: RedemittelCategory,
  ): Array<{ german: string; english: string }> => {
    const allPhrases: Array<{ german: string; english: string }> = [];

    // Add general sentences
    category.sentences.forEach((sentence) => {
      allPhrases.push(parseSentence(sentence));
    });

    // Add subsection sentences
    category.subsections?.forEach((subsection) => {
      subsection.sentences.forEach((sentence) => {
        allPhrases.push(parseSentence(sentence));
      });
    });

    return allPhrases;
  };

  // Filter phrases by search query
  const filterPhrases = (
    phrases: Array<{ german: string; english: string }>,
    query: string,
  ): Array<{ german: string; english: string }> => {
    if (!query) return phrases;
    const lowerQuery = query.toLowerCase();
    return phrases.filter(
      (phrase) =>
        phrase.german.toLowerCase().includes(lowerQuery) ||
        phrase.english.toLowerCase().includes(lowerQuery),
    );
  };

  const shellMainClass =
    "mx-auto w-full max-w-7xl flex-1 px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-8";

  if (loading) {
    return (
      <StandardPageShell
        title={t("learning.sentences.title", {
          defaultValue: "Common Sentences (Redemittel)",
        })}
        headerRight={<LanguageSwitcher />}
        className={shellMainClass}
      >
        <div>
          <LearningSectionSwitcher active="sentences" />
          <div className="flex min-h-[40vh] items-center justify-center">
            <p className="font-ui text-muted-foreground">
              {t("common.loading", { defaultValue: "Loading sentences..." })}
            </p>
          </div>
        </div>
      </StandardPageShell>
    );
  }

  return (
    <StandardPageShell
      title={t("learning.sentences.title", {
        defaultValue: "Common Sentences (Redemittel)",
      })}
      headerRight={<LanguageSwitcher />}
      className={shellMainClass}
    >
      <div>
        <LearningSectionSwitcher active="sentences" />
        <div className="mb-6">
          <p className="font-ui mb-6 text-sm text-muted-foreground">
            {t("learning.sentences.subtitle", {
              defaultValue: "Essential phrases and expressions for every level",
            })}
          </p>

          <div className="mb-4 flex items-center gap-4">
            <SearchBar
              placeholder={t("learning.sentences.searchPlaceholder", {
                defaultValue: "Search phrases…",
              })}
              onSearch={setSearchQuery}
              className="max-w-none w-full"
            />
          </div>

          <div className="rounded-xl border border-border bg-card p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="font-ui text-sm font-medium text-foreground">
                {t("learning.sidebar.level", { defaultValue: "Level" })}
              </p>
              {selectedLevels.length > 0 && (
                <Button variant="ghost" size="sm" onClick={handleClearLevels}>
                  {t("learning.clearFilters", {
                    defaultValue: "Clear filters",
                  })}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {LEVEL_ORDER.map((level) => {
                const selected = selectedLevels.includes(level);
                const locked = isLevelLocked(level);
                return (
                  <Button
                    key={level}
                    type="button"
                    variant={selected ? "default" : "outline"}
                    size="sm"
                    aria-pressed={selected}
                    aria-disabled={locked}
                    onClick={() => !locked && handleLevelToggle(level)}
                    className={cn("gap-2", locked && "cursor-not-allowed")}
                  >
                    <span>{level}</span>
                    {locked && <Lock className="h-3 w-3" />}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>

        {filteredLevelRows.map((row) => {
          if (row.kind === "comingSoon") {
            const displayName = levelDisplayName(row.level);
            return (
              <div key={row.level} className={cn("mb-4", "opacity-75")}>
                <div
                  className={cn(
                    "w-full flex items-center justify-between p-4 bg-card border-2 border-border rounded-xl",
                    "cursor-default grayscale-[0.5]",
                  )}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="px-3 py-1 rounded-full font-ui font-semibold text-sm shrink-0 bg-muted text-muted-foreground">
                      {row.level}
                    </span>
                    <div className="text-left min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-ui font-semibold text-lg text-foreground">
                          {displayName}
                        </h3>
                        <div className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-500 border border-amber-500/20">
                          <Lock className="h-3 w-3 shrink-0" />
                          <span className="text-[10px] font-bold uppercase tracking-wider">
                            {t("common.comingSoon", {
                              defaultValue: "Coming Soon",
                            })}
                          </span>
                        </div>
                      </div>
                      <p className="font-ui text-sm text-muted-foreground">
                        {t("learning.sentences.comingSoon", {
                          defaultValue:
                            "Coming soon - Advanced content in development",
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          const levelData = row;
          const isExpanded = expandedLevels.has(levelData.level);
          const displayName = levelDisplayName(levelData.level);

          return (
            <div key={levelData.level} className="mb-4">
              <button
                type="button"
                onClick={() => toggleLevelExpanded(levelData.level)}
                className={cn(
                  "w-full flex items-center justify-between p-4 bg-card border-2 border-border rounded-xl transition-colors",
                  "hover:border-primary/50 cursor-pointer",
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "px-3 py-1 rounded-full font-ui font-semibold text-sm",
                      "bg-primary/10 text-primary",
                    )}
                  >
                    {levelData.level}
                  </span>
                  <div className="text-left">
                    <h3 className="font-ui font-semibold text-lg text-foreground">
                      {displayName}
                    </h3>
                    <p className="font-ui text-sm text-muted-foreground">
                      {t("learning.categories", {
                        count: levelData.categories.length,
                        defaultValue: `${levelData.categories.length} categories`,
                      })}
                    </p>
                  </div>
                </div>
                {isExpanded ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
                )}
              </button>

              {isExpanded && (
                <div className="mt-2 space-y-2">
                  {levelData.categories.map((category, catIndex) => {
                    const categoryId = `${levelData.level}-${catIndex}-${category.category}`;
                    const isCategoryExpanded =
                      expandedCategories.has(categoryId);
                    const allPhrases = parseCategoryPhrases(category);
                    const filteredPhrases = filterPhrases(
                      allPhrases,
                      searchQuery,
                    );

                    return (
                      <div
                        key={categoryId}
                        className="overflow-hidden rounded-lg border border-border bg-card shadow-sm"
                      >
                        <button
                          type="button"
                          onClick={() => toggleCategoryExpanded(categoryId)}
                          className={cn(
                            "w-full flex items-center justify-between gap-3 p-4 text-left transition-colors",
                            isCategoryExpanded
                              ? "border-b border-border bg-muted/70 dark:bg-muted/55"
                              : "bg-card hover:bg-accent/50",
                          )}
                        >
                          <h4 className="font-ui font-semibold text-base text-foreground">
                            {category.category}
                          </h4>
                          {isCategoryExpanded ? (
                            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                          )}
                        </button>

                        {isCategoryExpanded && (
                          <div
                            className={cn(
                              "border-l-4 border-l-primary/40 bg-muted/30 px-2 py-2 dark:bg-muted/25 sm:px-4",
                              "[&>div:nth-child(odd)]:bg-muted/20 [&>div:nth-child(even)]:bg-muted/5",
                              "dark:[&>div:nth-child(odd)]:bg-muted/35 dark:[&>div:nth-child(even)]:bg-muted/15",
                            )}
                          >
                            {filteredPhrases.length === 0 ? (
                              <div className="px-2 py-6 text-center font-ui text-sm text-muted-foreground sm:px-0">
                                {t("learning.sentences.noPhrases", {
                                  defaultValue: "No phrases found",
                                })}
                              </div>
                            ) : (
                              filteredPhrases.map((phrase, phraseIndex) => {
                                const phraseId = getPhraseId(
                                  phrase.german,
                                  categoryId,
                                  phraseIndex,
                                );
                                return (
                                  <PhraseItem
                                    key={phraseId}
                                    phrase={phrase}
                                    phraseId={phraseId}
                                    onCopy={handleCopy}
                                    onBookmarkToggle={handleBookmarkToggle}
                                    isBookmarked={bookmarkedPhrases.has(
                                      phraseId,
                                    )}
                                  />
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </StandardPageShell>
  );
}
